// A clue that marks possible airports on the map, one of them the
// destination. It takes the place of the weather on arrival, which the API
// still writes.

import { Airport } from "@/models/airport";
import { getDistance } from "@/utils/airport_distance";
import { bearing } from "./geo";
import { NEAR_MILES } from "./game";
import { dt } from "./strings";
import { DailyClue, DailyPuzzle } from "./types";

// How many airports are marked, the destination included.
export const POSSIBLE_AIRPORTS = 10;

// Some are close around the destination, so a guess that lands "near" still
// leaves several to choose between. They may be smaller airports, as there
// are few big ones in some places.
const CLOSE_COUNT = 4;
const CLOSE_BUSY_ENOUGH = 5;
const CLOSE_SPACING_MILES = 50;
// Airports this close to the destination are left out: they share its city.
const SAME_CITY_MILES = 40;

// The rest are ahead of the plane, about as far as the destination is, and
// busy enough that a player might guess them.
const BUSY_ENOUGH = 30;
const SPACING_MILES = 90;

export const withAirportsHint = (puzzle: DailyPuzzle): DailyPuzzle => {
  const clue: DailyClue = {
    type: "possible_airports",
    shortFact: dt("airportsShort", { count: POSSIBLE_AIRPORTS }),
    title: dt("airportsTitle", { count: POSSIBLE_AIRPORTS }),
    detail: dt("airportsDetail"),
  };
  const swapped = puzzle.clues.map((existing) => (existing.type === "on_arrival" ? clue : existing));
  const airline = swapped.find((existing) => existing.type === "airline");
  const withoutAirline = swapped.filter((existing) => existing !== airline);
  const at = withoutAirline.indexOf(clue);
  return {
    ...puzzle,
    clues: airline && at >= 0 ? [...withoutAirline.slice(0, at), airline, ...withoutAirline.slice(at)] : swapped,
  };
};

// A random number generator that gives the same run for the same seed, so
// every player of a puzzle sees the same airports.
const seededRandom = (seed: string) => {
  let state = [...seed].reduce((hash, char) => Math.imul(hash ^ char.charCodeAt(0), 16777619), 2166136261) >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const angleBetween = (a: number, b: number): number => {
  const difference = Math.abs(a - b) % 360;
  return difference > 180 ? 360 - difference : difference;
};

const shuffle = <T>(items: T[], random: () => number): T[] =>
  items
    .map((item) => ({ item, order: random() }))
    .sort((a, b) => a.order - b.order)
    .map(({ item }) => item);

// Takes airports in order while they are not within `spacing` of any taken.
const spreadOut = (candidates: Airport[], taken: Airport[], count: number, spacing: number): Airport[] => {
  const picked: Airport[] = [];
  for (const airport of candidates) {
    if (picked.length >= count) break;
    if ([...taken, ...picked].every((other) => getDistance(other, airport) >= spacing)) picked.push(airport);
  }
  return picked;
};

// The airports to mark, the destination listed among them in no particular
// place:
// - several close around the destination, in every direction, so a near
//   guess does not single it out;
// - the rest ahead of the plane, about as far from it as the destination,
//   so the time left does not single it out either, topped up with those
//   nearest the destination where there are too few.
export const possibleAirports = (puzzle: DailyPuzzle, airports: Airport[]): Airport[] => {
  const { origin, destination, position } = puzzle;
  if (airports.length === 0) return [];

  const random = seededRandom(`${puzzle.date}-${destination.iata}`);
  const pool = shuffle(
    airports.filter(
      (airport) =>
        airport.iata !== origin.iata &&
        getDistance(destination, airport) >= SAME_CITY_MILES &&
        getDistance(origin, airport) >= SAME_CITY_MILES
    ),
    random
  );

  const close = spreadOut(
    pool.filter(
      (airport) =>
        airport.daily_departures >= CLOSE_BUSY_ENOUGH && getDistance(destination, airport) <= NEAR_MILES
    ),
    [destination],
    CLOSE_COUNT,
    CLOSE_SPACING_MILES
  );

  // Start with a band of distances and directions around the destination's,
  // seen from the plane, and loosen, though never to behind the plane.
  const remaining = getDistance(position, destination);
  const heading = bearing(position, destination);
  const wanted = POSSIBLE_AIRPORTS - 1 - close.length;
  const looser = [
    { distance: 0.5, angle: 45 },
    { distance: 0.7, angle: 65 },
    { distance: 0.9, angle: 90 },
  ];
  const busy = pool.filter((airport) => airport.daily_departures >= BUSY_ENOUGH && !close.includes(airport));
  let ahead: Airport[] = [];
  for (const tolerance of looser) {
    const fitting = busy.filter(
      (airport) =>
        Math.abs(getDistance(position, airport) - remaining) <= Math.max(remaining * tolerance.distance, 300) &&
        angleBetween(bearing(position, airport), heading) <= tolerance.angle
    );
    ahead = spreadOut(fitting, [destination, ...close], wanted, SPACING_MILES);
    if (ahead.length >= wanted) break;
  }

  // Where there are too few big airports ahead, as over open sea, the rest
  // are the airports nearest the destination, whatever their size.
  const nearest = [...pool]
    .filter((airport) => !close.includes(airport) && !ahead.includes(airport))
    .sort((a, b) => getDistance(destination, a) - getDistance(destination, b));
  const filler = spreadOut(nearest, [destination, ...close, ...ahead], wanted - ahead.length, CLOSE_SPACING_MILES);

  const others = shuffle([...close, ...ahead, ...filler], random);
  const place = Math.floor(random() * (others.length + 1));
  return [...others.slice(0, place), destination, ...others.slice(place)];
};
